import { create } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';

import { type Task, TaskSchema } from '@shaojiang/types/todo';

const DEFAULT_TASKS: Task[] = [
  { key: 0, done: true, content: 'List projects' },
  { key: 1, done: true, content: 'Write article about code formatting' },
  { key: 2, done: false, content: 'Fill in i18n content' },
  { key: 3, done: false, content: 'Add prerequisites to articles' },
  { key: 4, done: false, content: 'Add tool: color picker' },
  { key: 5, done: false, content: 'Add tool: svg2base64' },
  { key: 6, done: false, content: 'Auth0' },
  { key: 7, done: false, content: 'Put static images to S3 and CloudFlare' },
  { key: 8, done: false, content: 'List bookmarks' },
  { key: 9, done: false, content: 'Enable markdown pages for privacy and terms' },
  { key: 10, done: false, content: 'Show case: Image Player' },
  { key: 11, done: false, content: 'Show case: Mapbox' },
  { key: 12, done: false, content: 'More themes' },
  { key: 13, done: false, content: 'Algorithm visualization' },
];

/** The Redux version stored a bare array under `tasks`; wrap it in zustand's persist shape. */
const legacyAwareStorage: StateStorage = {
  getItem: (name) => {
    const value = localStorage.getItem(name);
    try {
      const parsed = value ? JSON.parse(value) : null;
      return Array.isArray(parsed)
        ? JSON.stringify({ state: { tasks: parsed }, version: 0 })
        : value;
    } catch {
      return null;
    }
  },
  setItem: (name, value) => localStorage.setItem(name, value),
  removeItem: (name) => localStorage.removeItem(name),
};

interface TodoState {
  tasks: Task[];
  addTask: (content: string) => void;
  toggleTaskDone: (key: number) => void;
}

export const useTodoStore = create<TodoState>()(
  persist(
    (set) => ({
      tasks: DEFAULT_TASKS,
      addTask: (content) =>
        set((state) => ({
          tasks: [
            ...state.tasks,
            { key: Math.max(-1, ...state.tasks.map((t) => t.key)) + 1, done: false, content },
          ],
        })),
      toggleTaskDone: (key) =>
        set((state) => ({
          tasks: state.tasks.map((task) =>
            task.key === key ? { ...task, done: !task.done } : task,
          ),
        })),
    }),
    {
      // Same key the Redux version wrote, so existing visitors keep their list
      name: 'tasks',
      storage: createJSONStorage(() => legacyAwareStorage),
      partialize: (state) => ({ tasks: state.tasks }),
      // Validate whatever was in localStorage; fall back to defaults if it's malformed
      merge: (persisted, current) => {
        const parsed = TaskSchema.array().safeParse((persisted as { tasks?: unknown })?.tasks);
        return parsed.success ? { ...current, tasks: parsed.data } : current;
      },
    },
  ),
);
